import React, { useEffect, useMemo, useState } from "react";
import {
  completeParaplanningCase,
  deleteParaplanningCase,
  markParaplanningCaseAsNpw,
} from "../services/paraplanningCasesService";
import { formatCurrency } from "../utils/formatCurrency";
import "../../../css/Praplanning/ParaplanningDashboard.css";
import "../../../css/Praplanning/ParaplanningCasesTable.css";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import { getParaplanningPermissionState, isParaplanningUser } from "../utils/userPermissions";

const PAGE_SIZE = 50;

const DEFAULT_SORT_CONFIG = {
  key: "startDate",
  direction: "desc",
};

const formatDate = (value, options = {}) => {
  if (!value) return "-";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    weekday: options.showDay ? "short" : undefined,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toDateValue = (value) => {
  if (!value) return 0;

  if (value?.toDate) {
    return value.toDate().getTime();
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 0;
  }

  return date.getTime();
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatStatusLabel = (status) => {
  if (status === "completed") return "Completed";
  if (status === "cancelled") return "Cancelled";
  if (status === "npw") return "NPW";
  return "In Progress";
};

const getStatusClass = (status) => {
  if (status === "completed") return "ParaplanningTableStatus--Completed";
  if (status === "cancelled") return "ParaplanningTableStatus--Cancelled";
  if (status === "npw") return "ParaplanningTableStatus--NPW";
  return "ParaplanningTableStatus--InProgress";
};

const getClientNames = (item) => {
  if (Array.isArray(item.clients) && item.clients.length) {
    return item.clients
      .map(
        (client) =>
          client.name ||
          `${client.firstName || ""} ${client.lastName || ""}`.trim(),
      )
      .filter(Boolean)
      .join(" + ");
  }

  return item.client?.name || "-";
};

const getStartDate = (item) => {
  return item.startDate || item.createdAt;
};

const getUniqueChargeHeadings = (item) => {
  const possibleCharges = [
    ...(Array.isArray(item.chargeSnapshot?.modifiers)
      ? item.chargeSnapshot.modifiers
      : []),
    ...(Array.isArray(item.modifiers) ? item.modifiers : []),
    ...(Array.isArray(item.additionalCharges) ? item.additionalCharges : []),
    ...(Array.isArray(item.customCharges) ? item.customCharges : []),
    ...(Array.isArray(item.costs?.additionalCharges)
      ? item.costs.additionalCharges
      : []),
  ];

  const labels = possibleCharges
    .map((charge) =>
      String(
        charge?.heading ||
          charge?.label ||
          charge?.name ||
          charge?.title ||
          charge?.question ||
          charge?.itemName ||
          "",
      ).trim(),
    )
    .filter(Boolean);

  return [...new Set(labels)];
};

const getType = (item) => {
  const baseType = String(
    item.type || item.chargeSnapshot?.itemName || "-",
  ).trim();

  const uniqueChargeHeadings = getUniqueChargeHeadings(item).filter(
    (heading) => heading.toLowerCase() !== baseType.toLowerCase(),
  );

  if (!uniqueChargeHeadings.length) {
    return baseType || "-";
  }

  return [baseType, ...uniqueChargeHeadings].filter(Boolean).join(" + ");
};

const getCatalogName = (item) => {
  return item.chargeSnapshot?.catalogName || item.catalogId || "Case type";
};

const getTotalCostValue = (item) => {
  if (item.status !== "completed") return 0;

  const value = item.costs?.totalCost;

  if (value === undefined || value === null || value === "") return 0;

  return Number(value) || 0;
};

const getTotalCost = (item) => {
  if (item.status !== "completed") return "-";

  const value = item.costs?.totalCost;

  if (value === undefined || value === null || value === "") return "-";

  return formatCurrency(value);
};

const getAssignedTo = (item) => {
  return item.assignedTo?.name || item.assignedTo?.email || "-";
};

const getNotes = (item) => {
  return item.notes || "-";
};

const getReferenceNumber = (item) => {
  return (
    item.id ||
    item.referenceNumber ||
    item.client?.reference ||
    item.reference ||
    item.clients?.[0]?.reference ||
    "-"
  );
};

const getPriorityLabel = (item) => {
  return item?.highPriority ? "High" : "Normal";
};

const getEstimatedTurnaround = (item) => {
  const startValue = getStartDate(item);
  if (!startValue) return "-";

  const startDate = startValue?.toDate
    ? startValue.toDate()
    : new Date(startValue);
  if (Number.isNaN(startDate.getTime())) return "-";

  const estimatedDate = new Date(startDate);
  estimatedDate.setDate(estimatedDate.getDate() + 5);

  return formatDate(estimatedDate);
};

const getLastEditedBy = (item) => {
  return (
    item.lastEditedBy?.name ||
    item.lastEditedBy?.email ||
    item.createdBy?.name ||
    item.createdBy?.email ||
    "-"
  );
};

const getEditedLabel = (item) => {
  if (item.lastEditedBy?.name || item.lastEditedBy?.email) {
    return "Last Edited";
  }

  return "Created By";
};

const getLastEditedDate = (item) => {
  return item.updatedAt || item.createdAt
    ? formatDate(item.updatedAt || item.createdAt)
    : "-";
};

const getChargeRows = (item) => {
  const modifiers = item.chargeSnapshot?.modifiers || [];

  return modifiers.map((modifier) => ({
    id: modifier.id,
    label: modifier.label || modifier.name || "-",
    quantity: Number(modifier.quantity || 1),
    amount: Number(modifier.amount || 0),
    isDiscount: Boolean(modifier.isDiscount),
  }));
};

const getTasks = (item) => {
  if (Array.isArray(item.tasksForCase) && item.tasksForCase.length) {
    return item.tasksForCase;
  }

  if (Array.isArray(item.taskIds) && item.taskIds.length) {
    return item.taskIds;
  }

  return [];
};

const getSortValue = (item, key) => {
  if (key === "startDate") return toDateValue(getStartDate(item));
  if (key === "adviser") return item.adviser?.name || "";
  if (key === "client") return getClientNames(item);
  if (key === "type") return getType(item);
  if (key === "completionDate") return toDateValue(item.completionDate);
  if (key === "totalCost") return getTotalCostValue(item);
  if (key === "status") return item.status || "in_progress";

  return "";
};

const hasCompletedOnDate = (item) => {
  return item.status === "completed" || item.status === "npw";
};

const getTurnaroundDays = (item) => {
  if (!hasCompletedOnDate(item)) return null;

  const start = toDateValue(getStartDate(item));
  const end = toDateValue(item.completionDate);

  if (!start || !end || end < start) return null;

  return (end - start) / DAY_IN_MS;
};

const getAverageTurnaroundDays = (cases = []) => {
  const turnaroundValues = cases
    .map((item) => getTurnaroundDays(item))
    .filter((value) => typeof value === "number");

  if (!turnaroundValues.length) return null;

  const total = turnaroundValues.reduce((sum, value) => sum + value, 0);
  return Number((total / turnaroundValues.length).toFixed(1));
};

const formatTurnaroundDays = (days) => {
  if (days === null || days === undefined) return "-";

  const rounded = Number(days);
  const abs = Math.abs(rounded);
  const unit = abs === 1 ? "day" : "days";

  return `${rounded} ${unit}`;
};

const sortCases = (cases = [], sortConfig) => {
  const { key, direction } = sortConfig;

  return [...cases].sort((a, b) => {
    const aValue = getSortValue(a, key);
    const bValue = getSortValue(b, key);

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    const result = String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return direction === "asc" ? result : -result;
  });
};

const ConfirmationPopup = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  variant = "default",
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <div className="ParaplanningPopupOverlay">
      <div className="ParaplanningPopup">
        <div className="ParaplanningPopup__Title">{title}</div>
        <div className="ParaplanningPopup__Message">{message}</div>

        <div className="ParaplanningPopup__Actions">
          <button
            type="button"
            className="ParaplanningPopup__Cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            className={`ParaplanningPopup__Confirm ParaplanningPopup__Confirm--${variant}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Please wait..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const MessagePopup = ({ open, title, message, onClose }) => {
  if (!open) return null;

  return (
    <div className="ParaplanningPopupOverlay">
      <div className="ParaplanningPopup">
        <div className="ParaplanningPopup__Title">{title}</div>
        <div className="ParaplanningPopup__Message">{message}</div>

        <div className="ParaplanningPopup__Actions">
          <button
            type="button"
            className="ParaplanningPopup__Confirm"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const TableCell = ({ main, sub, money = false }) => {
  return (
    <div className="ParaplanningTableCell">
      <span
        className={`ParaplanningTableCell__Main ${
          money ? "ParaplanningTableCell__Main--Money" : ""
        } FP_Content black`}
      >
        {main || "-"}
      </span>

      {sub && <span className="ParaplanningTableCell__Sub">{sub}</span>}
    </div>
  );
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === sortKey;

  return (
    <button
      type="button"
      className={`ParaplanningTableHeaderButton ${
        isActive ? "ParaplanningTableHeaderButton--Active" : ""
      }`}
      onClick={() => onSort(sortKey)}
    >
      <span className="FP_Content">{label}</span>

      <span
        className={`ParaplanningTable__SortIcon ${
          isActive && sortConfig.direction === "desc"
            ? "ParaplanningTable__SortIcon--Desc"
            : ""
        }`}
      >
        <SortIcon />
      </span>
    </button>
  );
};

const getPaginationItems = (currentPage, totalPages) => {
  const delta = 1;
  const pages = [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  pages.push(1);

  const start = Math.max(2, currentPage - delta);
  const end = Math.min(totalPages - 1, currentPage + delta);

  if (start > 2) {
    pages.push("start-ellipsis");
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < totalPages - 1) {
    pages.push("end-ellipsis");
  }

  pages.push(totalPages);

  return pages;
};

const ParaplanningCasesTable = ({
  cases = [],
  loading,
  onRefresh,
  onEditCase,
  loggedInEmail,
  loggedInParaplannerName,
  tableMode = "assigned",
  emptyMessage = "No cases found.",
  initialExpandedCaseId = "",
}) => {
  const [expandedCaseIds, setExpandedCaseIds] = useState([]);
  useEffect(() => {
    if (initialExpandedCaseId && cases?.some((item) => item.id === initialExpandedCaseId)) {
      setExpandedCaseIds([initialExpandedCaseId]);
    }
  }, [cases, initialExpandedCaseId]);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { userPermissions = [] } = useUserPermissions();

  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const {
    isAdmin,
    isAccountsAdmin,
    isViewOnly,
    isParaplanner,
    canView,
    canManageCases,
    canManageAllCases,
  } = getParaplanningPermissionState(userPermissions);
  const isUnassignedTable = tableMode === "unassigned";

  const resetSort = () => {
    setSortConfig(DEFAULT_SORT_CONFIG);
    setCurrentPage(1);
    setExpandedCaseIds([]);
  };

  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    type: "",
    caseId: "",
    title: "",
    message: "",
  });

  const [messagePopup, setMessagePopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const sortedCases = useMemo(() => {
    const sorted = sortCases(cases, sortConfig);
    if (!initialExpandedCaseId) return sorted;
    const linkedIndex = sorted.findIndex((item) => item.id === initialExpandedCaseId);
    if (linkedIndex < 0) return sorted;
    return [sorted[linkedIndex], ...sorted.slice(0, linkedIndex), ...sorted.slice(linkedIndex + 1)];
  }, [cases, sortConfig, initialExpandedCaseId]);

  useEffect(() => {
    if (initialExpandedCaseId) setCurrentPage(1);
  }, [initialExpandedCaseId]);

  const averageTurnaroundDays = useMemo(() => {
    return getAverageTurnaroundDays(cases);
  }, [cases]);

  const totalPages = Math.max(Math.ceil(sortedCases.length / PAGE_SIZE), 1);

  const paginatedCases = useMemo(() => {
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;

    return sortedCases.slice(startIndex, startIndex + PAGE_SIZE);
  }, [sortedCases, currentPage, totalPages]);

  const pageStart = sortedCases.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, sortedCases.length);
  const paginationItems = getPaginationItems(currentPage, totalPages);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return {
          key,
          direction: "asc",
        };
      }

      if (prev.direction === "asc") {
        return {
          key,
          direction: "desc",
        };
      }

      return DEFAULT_SORT_CONFIG;
    });

    setCurrentPage(1);
    setExpandedCaseIds([]);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    setExpandedCaseIds([]);
  };
  const toggleExpanded = (caseId) => {
    setExpandedCaseIds((prev) =>
      prev.includes(caseId)
        ? prev.filter((id) => id !== caseId)
        : [...prev, caseId],
    );
  };
  const closeConfirmPopup = () => {
    if (actionLoading) return;

    setConfirmPopup({
      open: false,
      type: "",
      caseId: "",
      title: "",
      message: "",
    });
  };

  const showMessage = (title, message) => {
    setMessagePopup({
      open: true,
      title,
      message,
    });
  };

  const closeMessage = () => {
    setMessagePopup({
      open: false,
      title: "",
      message: "",
    });
  };

  const requestComplete = (caseId) => {
    setConfirmPopup({
      open: true,
      type: "complete",
      caseId,
      title: "Complete case?",
      message:
        "This will mark the case as completed and set the completion date to today.",
    });
  };

  // const requestDelete = (caseId) => {
  //   setConfirmPopup({
  //     open: true,
  //     type: "delete",
  //     caseId,
  //     title: "Delete case?",
  //     message:
  //       "Are you sure you want to delete this case? This action cannot be undone.",
  //   });
  // };

  const requestNpw = (caseId) => {
    setConfirmPopup({
      open: true,
      type: "npw",
      caseId,
      title: "Mark as NPW?",
      message:
        "This will mark the case as Not Proceeding With. The case will remain on the dashboard.",
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmPopup.caseId || !confirmPopup.type) return;

    try {
      setActionLoading(true);

      if (confirmPopup.type === "complete") {
        await completeParaplanningCase(confirmPopup.caseId, {
          name: loggedInParaplannerName,
          email: loggedInEmail,
        });

        showMessage("Case completed", "The case has been marked as completed.");
      }

      // if (confirmPopup.type === "delete") {
      //   await deleteParaplanningCase(confirmPopup.caseId);
      //   showMessage("Case deleted", "The case has been deleted.");
      // }

      if (confirmPopup.type === "npw") {
        await markParaplanningCaseAsNpw(confirmPopup.caseId, {
          name: loggedInParaplannerName,
          email: loggedInEmail,
        });

        showMessage(
          "Case marked as NPW",
          "The case has been marked as Not Proceeding With.",
        );
      }

      setConfirmPopup({
        open: false,
        type: "",
        caseId: "",
        title: "",
        message: "",
      });

      setExpandedCaseIds([]);
      onRefresh?.();
    } catch (error) {
      console.error(error);

      showMessage(
        "Something went wrong",
        error.message || "The action could not be completed.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="ParaplanningTableState">Loading cases...</div>;
  }

  if (!cases.length) {
    return <div className="ParaplanningTableState">{emptyMessage}</div>;
  }

  // console.log("isUnassignedTable", isUnassignedTable);

  return (
    <>
      <div>
        <div className="ParaplanningTableToolbar">
          <button
            type="button"
            className="ParaplanningTableToolbar__Button FP_Body"
            onClick={resetSort}
          >
            Reset Sort
          </button>

      
        </div>
        <table className="ParaplanningTable">
          <thead>
            <tr>
              <th>
                <SortableHeader
                  label="Start Date"
                  sortKey="startDate"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Adviser"
                  sortKey="adviser"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Client"
                  sortKey="client"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Type"
                  sortKey="type"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Completion Date"
                  sortKey="completionDate"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Total Cost"
                  sortKey="totalCost"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>

              <th>
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedCases.map((item) => {
              const isCompleted = item.status === "completed";
              const showCompletedOnDate = hasCompletedOnDate(item);
              const isExpanded = expandedCaseIds.includes(item.id);
              const chargeRows = getChargeRows(item);
              const tasks = getTasks(item);
              const isPipelineHighPriority =
                isUnassignedTable && Boolean(item.highPriority);

              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`
                      ${isExpanded ? "ParaplanningTableRow--Open" : ""}
                      ${
                        isPipelineHighPriority
                          ? "ParaplanningTableRow--PipelineHighPriority"
                          : ""
                      }
                    `.trim()}
                  >
                    <td>
                      <TableCell
                        main={formatDate(getStartDate(item), { showDay: true })}
                        // sub="Start date"
                      />
                    </td>

                    <td>
                      <TableCell
                        main={item.adviser?.name || "-"}
                        // sub={item.adviser?.email || "Adviser"}
                      />
                    </td>

                    <td>
                      <TableCell main={getClientNames(item)} />
                    </td>

                    <td>
                      <TableCell
                        main={getType(item)}
                        sub={
                          isAdmin || isAccountsAdmin || canView
                            ? `${getAssignedTo(item)} | ${getNotes(item)}`
                            : getNotes(item)
                        }
                      />
                    </td>

                    <td>
                      <TableCell
                        main={
                          showCompletedOnDate
                            ? formatDate(item.completionDate)
                            : "-"
                        }
                        // sub="Completion date"
                      />
                    </td>

                    <td>
                      <TableCell
                        main={getTotalCost(item)}
                        // sub="Total cost"
                        money
                      />
                    </td>

                    <td>
                      <div className="ParaplanningTableActions">
                        {isUnassignedTable ? (
                          <button
                            type="button"
                            className="ParaplanningTableActions__Assign"
                            onClick={() => onEditCase?.(item)}
                          >
                            Assign Case
                          </button>
                        ) : (
                          <span
                            className={`ParaplanningTableStatus ${getStatusClass(item.status)}`}
                          >
                            {formatStatusLabel(item.status)}
                          </span>
                        )}

                        <button
                          type="button"
                          className={`ParaplanningTableActions__Arrow ${
                            isExpanded
                              ? "ParaplanningTableActions__Arrow--Open"
                              : ""
                          }`}
                          aria-label={
                            isExpanded
                              ? "Close case details"
                              : "Open case details"
                          }
                          onClick={() => toggleExpanded(item.id)}
                        >
                          <ArrowDownIcon />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="ParaplanningTableDropdownRow">
                      <td colSpan={7}>
                        <div className="ParaplanningCaseCard">
                          <div className="ParaplanningCaseCard__Header">
                            <div>
                              <div className="FP_Help_Text black">
                                Additional Details
                              </div>

                              {/* <div className="ParaplanningCaseCard__Subtitle">
                                {getClientNames(item)} · {getType(item)}
                              </div> */}
                            </div>

                            {(isAccountsAdmin || isParaplanner || isAdmin) && (
                              <div className="ParaplanningCaseCard__HeaderActions">
                                <button
                                  type="button"
                                  className="ParaplanningCaseCard__ActionButton"
                                  onClick={() => onEditCase?.(item)}
                                >
                                  <EditIcon />
                                  <span>
                                    {isUnassignedTable ? "Assign Case" : "Edit"}
                                  </span>
                                </button>

                                {!isCompleted && !isUnassignedTable && (
                                  <button
                                    type="button"
                                    className="ParaplanningCaseCard__ActionButton ParaplanningCaseCard__ActionButton--Complete"
                                    onClick={() => requestComplete(item.id)}
                                  >
                                    Complete
                                  </button>
                                )}

                                {/* <button
                                  type="button"
                                  className="ParaplanningCaseCard__ActionButton ParaplanningCaseCard__ActionButton--Delete"
                                  onClick={() => requestDelete(item.id)}
                                >
                                  <DeleteIcon />
                                  <span>Delete</span>
                                </button> */}
                                {!isUnassignedTable &&
                                  item.status !== "npw" && (
                                    <button
                                      type="button"
                                      className="ParaplanningCaseCard__ActionButton ParaplanningCaseCard__ActionButton--NPW"
                                      onClick={() => requestNpw(item.id)}
                                    >
                                      {/* <DeleteIcon /> */}
                                      <span>NPW</span>
                                    </button>
                                  )}
                              </div>
                            )}
                          </div>

                          <div className="ParaplanningCaseCard__Grid">
                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Notes
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {getNotes(item)}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Assigned To
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {getAssignedTo(item)}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Base Cost
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {isCompleted
                                  ? formatCurrency(item.costs?.baseCost || 0)
                                  : "-"}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Additional Charges
                              </div>

                              <div className="ParaplanningCaseCard__Value">
                                {chargeRows.length
                                  ? chargeRows.map((charge) => (
                                      <div
                                        key={charge.id}
                                        className="ParaplanningCaseCard__Charge"
                                      >
                                        <span>{charge.label}</span>
                                        <strong>
                                          {charge.isDiscount ? "-" : ""}
                                          {formatCurrency(charge.amount)} x{" "}
                                          {charge.quantity}
                                        </strong>
                                      </div>
                                    ))
                                  : "-"}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Tasks
                              </div>

                              <div className="ParaplanningCaseCard__Value">
                                {tasks.length ? (
                                  <div className="ParaplanningCaseCard__Tasks">
                                    {tasks.map((task, index) => (
                                      <span
                                        key={`${task}_${index}`}
                                        className="ParaplanningCaseCard__Task"
                                      >
                                        {task}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                {getEditedLabel(item)}
                              </div>

                              <div className="ParaplanningCaseCard__Value">
                                <div>{getLastEditedDate(item)}</div>

                                <div className="ParaplanningCaseCard__Muted">
                                  By {getLastEditedBy(item)}
                                </div>
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Completed On
                              </div>

                              <div className="ParaplanningCaseCard__Value">
                                {showCompletedOnDate
                                  ? formatDate(item.completionDate)
                                  : "-"}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Reference Number
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {getReferenceNumber(item)}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Priority
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {getPriorityLabel(item)}
                              </div>
                            </div>

                            <div className="ParaplanningCaseCard__Detail">
                              <div className="ParaplanningCaseCard__Label">
                                Estimated Turnaround
                              </div>
                              <div className="ParaplanningCaseCard__Value">
                                {getEstimatedTurnaround(item)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="ParaplanningTablePagination">
          <div className="ParaplanningTablePagination__Info">
            Showing {pageStart}-{pageEnd} of {sortedCases.length}
          </div>

          <div className="ParaplanningTablePagination__Actions">
            <button
              type="button"
              className="ParaplanningTablePagination__Button"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Previous
            </button>

            {paginationItems.map((item) => {
              if (typeof item === "string") {
                return (
                  <span
                    key={item}
                    className="ParaplanningTablePagination__Ellipsis"
                  >
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={item}
                  type="button"
                  className={`ParaplanningTablePagination__Page ${
                    currentPage === item
                      ? "ParaplanningTablePagination__Page--Active"
                      : ""
                  }`}
                  onClick={() => goToPage(item)}
                >
                  {item}
                </button>
              );
            })}

            <button
              type="button"
              className="ParaplanningTablePagination__Button"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ConfirmationPopup
        open={confirmPopup.open}
        title={confirmPopup.title}
        message={confirmPopup.message}
        confirmLabel={confirmPopup.type === "npw" ? "Mark as NPW" : "Complete"}
        variant={confirmPopup.type === "npw" ? "danger" : "default"}
        // confirmLabel={confirmPopup.type === "delete" ? "Delete" : "Complete"}
        // variant={confirmPopup.type === "delete" ? "danger" : "default"}
        loading={actionLoading}
        onCancel={closeConfirmPopup}
        onConfirm={handleConfirmAction}
      />

      <MessagePopup
        open={messagePopup.open}
        title={messagePopup.title}
        message={messagePopup.message}
        onClose={closeMessage}
      />
    </>
  );
};

export default ParaplanningCasesTable;
